import { useDispatch, useSelector } from "react-redux";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { AddPackagesAction, SetInstalledPackagesAction } from "../reducer/package";
import { Package, PackageInstall } from "../types/package";
import { newPackageBlock } from "../types/notebook/note/block/package";
import { useInsertBlock } from "./page/block";
import { useSelectedNotebookPageIdMap } from "./page";
import { useAppIdMap } from "./app";
import { insertPageBlock } from "../api/page";
import { searchPackages, getPackage } from "../api/package";
import { writeData } from "../api";
import { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";

export const useAddPackages = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (packages: Package[]) => {
    const action: AddPackagesAction = {
      type: ActionType.ADD_PACKAGES,
      packages,
    };
    dispatch(action as any);
  };
};

export const useSetInstalledPackages = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (installs: PackageInstall[]) => {
    const action: SetInstalledPackagesAction = {
      type: ActionType.SET_INSTALLED_PACKAGES,
      installs,
    };
    dispatch(action as any);
  };
};

export const usePackages = () => {
  return useSelector((state: RootState) => state.ui.packages);
};

export const useInstalledPackages = () => {
  return useSelector((state: RootState) => state.ui.installedPackages);
};

export const useInsertPackageBlock = () => {
  const { selectedNotebookPage } = useSelectedNotebookPageIdMap();
  const { appId } = useAppIdMap();
  const insertBlock = useInsertBlock();
  const addPackages = useAddPackages();

  return (config: { blockIndex: number }) => {
    const { blockIndex } = config;
    const packageId = uuidv4();
    const pkg: Package = {
      "package-id": packageId,
      "app-id": appId!,
      "page-id": selectedNotebookPage!["page-id"],
      name: "Package",
    };
    const block = newPackageBlock({
      packageId,
      blockIndex,
      pageId: selectedNotebookPage!["page-id"],
      appId: appId!,
    });
    addPackages([pkg]);
    insertBlock({ block, blockIndex });
    insertPageBlock({ block, "block-index": blockIndex });
    writeData(pkg);
  };
};

export const usePackage = (packageId: string | undefined) => {
  const addPackages = useAddPackages();
  const { appId } = useAppIdMap();
  const pkg = useSelector((state: RootState) => {
    if (!packageId) return undefined;
    return state.ui.packages[packageId];
  });

  useEffect(() => {
    // Package data is loaded via list/fetch calls — no individual fetch API defined yet.
    // If the package is not in state, it will be populated when the packages list loads.
  }, [packageId]);

  return pkg;
};

export const useFetchPackage = () => {
  const addPackages = useAddPackages();

  return (packageId: string) => {
    return getPackage({ "package-id": packageId }).then((pkg) => {
      addPackages([pkg]);
      return pkg;
    });
  };
};

export const useFetchPackageListener = (packageId: string | undefined) => {
  const fetchPackage = useFetchPackage();
  useEffect(() => {
    if (!packageId) return;
    fetchPackage(packageId);
  }, [packageId]);
};

export const useSetPackageName = (packageId: string) => {
  const addPackages = useAddPackages();
  const pkg = useSelector(
    (state: RootState) => state.ui.packages[packageId],
  );

  const writeBackend = useMemo(
    () =>
      debounce((pkg: Package) => {
        writeData(pkg);
      }, 500),
    [packageId],
  );

  return (name: string) => {
    if (!pkg) return;
    const updated: Package = { ...pkg, name };
    addPackages([updated]);
    writeBackend(updated);
  };
};

export const useSetPackageDescription = (packageId: string) => {
  const addPackages = useAddPackages();
  const pkg = useSelector(
    (state: RootState) => state.ui.packages[packageId],
  );

  const writeBackend = useMemo(
    () =>
      debounce((pkg: Package) => {
        writeData(pkg);
      }, 500),
    [packageId],
  );

  return (description: string) => {
    if (!pkg) return;
    const updated: Package = { ...pkg, description };
    addPackages([updated]);
    writeBackend(updated);
  };
};

export const usePackageSearch = (config: { disabledSearch?: boolean } = {}) => {
  const { disabledSearch } = config;
  const [searchString, setSearchString] = useState<string>("");
  const [page, setPage] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const addPackages = useAddPackages();

  const handleSearch = useMemo(
    () =>
      debounce((s: string) => {
        setIsLoading(true);
        searchPackages({ search: s }).then((pkgs) => {
          setPage(pkgs ?? []);
          addPackages(pkgs ?? []);
          setIsLoading(false);
        });
      }, 200),
    [],
  );

  useEffect(() => {
    if (!disabledSearch) handleSearch(searchString);
  }, [searchString, disabledSearch]);

  return { searchString, setSearchString, isLoading, setIsLoading, page };
};
